import { useState } from "react";
import { Globe2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CopyButton, EmptyState, ErrorBanner, Notice, PageHeader, Spinner, StatusBadge, fmt, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";

const CHECK_STYLE = { found: "found", missing: "missing", wrong_value: "wrong_value", unknown: "lookup_timeout" };

function DnsGuide({ d }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/app/domains/${d.id}/dns-check`); setRes(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const steps = [
    "Sign in to the DNS provider where this domain is managed (e.g. Cloudflare, Route 53, your registrar).",
    `Add the ${d.dns_records.length} CNAME records below exactly as shown. If your provider appends the domain automatically, enter only the part before .${d.name}. Turn off proxying (DNS only).`,
    "Click \"Check DNS now\" — each record turns green when it is publicly visible. Propagation usually takes minutes, sometimes up to 48 hours.",
    "When all records are found, AWS confirms them on its own schedule (usually within an hour). The status here updates automatically; \"Check with AWS\" reads the latest result.",
  ];
  return (
    <div className="mt-5 rounded-xl border border-purple-500/20 bg-purple-950/10 p-5" data-testid="dns-guide">
      <div className="mb-3 text-sm font-semibold text-white">Set up DNS in 4 steps</div>
      <ol className="list-decimal space-y-1.5 pl-5 text-sm text-zinc-300">{steps.map((s) => <li key={s}>{s}</li>)}</ol>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={check} disabled={busy} data-testid="dns-check-button">{busy ? "Checking…" : "Check DNS now"}</Button>
        {res && <span className="text-xs text-zinc-400" data-testid="dns-check-summary">{res.all_found ? "All records found — click Check with AWS." : `${res.records.filter((r) => r.state === "found").length}/${res.records.length} records found · ${fmt(res.checked_at)}`}</span>}
      </div>
      {res && <ul className="mt-3 space-y-1.5">{res.records.map((r) => (
        <li key={r.name} className="flex flex-wrap items-center gap-2 font-mono text-[11px] text-zinc-400" data-testid="dns-check-record">
          <StatusBadge status={CHECK_STYLE[r.state]} /><span className="break-all text-zinc-300">{r.name}</span>
          {r.state === "wrong_value" && <span className="text-red-300">points to {r.found.join(", ")}</span>}
          {r.state === "missing" && <span className="text-zinc-500">not found yet</span>}
          {r.state === "unknown" && <span className="text-zinc-500">lookup timed out, try again</span>}
        </li>))}</ul>}
    </div>
  );
}

const AWS_ERRORS = {
  HOST_NOT_FOUND: "AWS couldn't see the DKIM records yet the last time it looked.",
  TYPE_NOT_FOUND: "AWS found the names but not as CNAME records.",
  INVALID_VALUE: "AWS found the records but they point to the wrong value.",
  SERVICE_ERROR: "AWS had a temporary problem while checking.",
  DNS_SERVER_ERROR: "Your DNS provider didn't answer AWS's lookup.",
  REPLICATION_ERROR: "AWS is still copying the identity between its regions.",
};

function AwsCheck({ aws }) {
  if (!aws?.last_checked_at) return null;
  return (
    <div className="mt-3" data-testid="domain-aws-check">
      <Notice tone="info" title={`AWS last checked your DNS ${fmt(aws.last_checked_at)}`}>
        {AWS_ERRORS[aws.error] || "AWS is still verifying the DKIM records."} AWS re-checks on its own schedule (usually within an hour, at most 72 hours) and CARIO Mail re-reads AWS every 10 minutes, so this domain turns verified automatically. Clicking "Check with AWS" only reads the latest result.
      </Notice>
    </div>
  );
}

function TrackingToggles({ d, setD, canAdmin }) {
  const [busy, setBusy] = useState(false);
  const tr = { opens: false, clicks: false, ...(d.tracking || {}) };
  const save = async (key, value) => {
    setBusy(true);
    try { const { data } = await api.patch(`/app/domains/${d.id}/tracking`, { ...tr, [key]: value }); setD(data); toast.success("Tracking updated"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const rows = [["opens", "Open tracking", "Adds an invisible 1×1 image to HTML emails"], ["clicks", "Click tracking", "Routes links through CARIO Mail to count clicks"]];
  return (
    <div className="mt-4 grid gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3 sm:grid-cols-2" data-testid={`domain-tracking-${d.name}`}>
      {rows.map(([key, label, hint]) => (
        <label key={key} className="flex items-start justify-between gap-3">
          <span><span className="block text-sm text-zinc-200">{label}</span><span className="block text-xs text-zinc-500">{hint}. Off by default; an email's track_{key} overrides it.</span></span>
          <Switch checked={tr[key]} disabled={!canAdmin || busy} onCheckedChange={(v) => save(key, v)} data-testid={`domain-tracking-${key}-switch`} />
        </label>
      ))}
    </div>
  );
}

function DomainCard({ d: initial, canAdmin, provisioning, reload }) {
  const [d, setD] = useState(initial);
  const [busy, setBusy] = useState(false);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const recheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/app/domains/${d.id}/refresh`);
      setD(data);
      if (data.status === "verified") toast.success("Verified by AWS — sending is unlocked");
      else toast.info("AWS hasn't confirmed the records yet. DNS results below are kept.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card testId={`domain-card-${d.name}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3"><span className="font-mono text-base text-white">{d.name}</span><StatusBadge status={d.status} testId={`domain-status-${d.name}`} /></div>
        {canAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy || !provisioning} onClick={recheck} className="border-white/10 bg-transparent hover:bg-white/5 hover:text-white" data-testid="domain-verify-button" title={provisioning ? "" : "AWS domain provisioning is not configured"}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" />Check with AWS</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => window.confirm(`Remove ${d.name}?`) && act(() => api.delete(`/app/domains/${d.id}`), "Domain removed")} className="text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="domain-delete-button"><Trash2 className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
      <div className="mt-3 text-xs text-zinc-500">
        Region {d.region || "—"} · Last checked {fmt(d.last_checked_at)}
        {d.verification_source && <> · Verified via <span className="text-zinc-300">{d.verification_source.replaceAll("_", " ")}</span></>}
      </div>
      <TrackingToggles d={d} setD={setD} canAdmin={canAdmin} />
      {d.note && <p className="mt-3 text-xs text-zinc-400">{d.note}</p>}
      {d.provider_error && <div className="mt-3"><Notice tone="error" title="AWS SES error">{d.provider_error}</Notice></div>}
      {d.status === "pending" && !d.ownership_review && <AwsCheck aws={d.aws_check} />}
      {d.status !== "verified" && d.dns_records?.length > 0 && <DnsGuide d={d} />}
      <div className="mt-5">
        {d.dns_records?.length ? (
          <div className="overflow-x-auto rounded-lg border border-white/10"><table className="w-full min-w-[600px] text-left text-xs">
            <thead className="bg-white/[0.02] uppercase tracking-wider text-zinc-500"><tr><th className="p-2.5">Type</th><th className="p-2.5">Name</th><th className="p-2.5">Value</th><th className="whitespace-nowrap p-2.5">AWS status</th></tr></thead>
            <tbody>{d.dns_records.map((r) => (
              <tr key={r.name} className="border-t border-white/5 font-mono">
                <td className="p-2.5 text-zinc-300">{r.type}</td>
                <td className="p-2.5 text-zinc-300"><span className="break-all">{r.name}</span> <CopyButton value={r.name} label="" testId="dns-copy-name" /></td>
                <td className="p-2.5 text-zinc-300"><span className="break-all">{r.value}</span> <CopyButton value={r.value} label="" testId="dns-copy-value" /></td>
                <td className="p-2.5"><StatusBadge status={r.status} /></td>
              </tr>))}</tbody>
          </table></div>
        ) : <p className="text-xs text-zinc-500" data-testid="dns-records-empty">No DNS records returned by the provider. CARIO Mail never invents DKIM records.</p>}
      </div>
    </Card>
  );
}

export default function Domains() {
  const { t } = useT();
  const { can } = useAuth();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { data, error, loading, reload } = useLoad(() => api.get("/app/domains").then((r) => r.data));
  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/app/domains", { name }); toast.success("Domain added"); setName(""); reload(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader title={t("nav.domains")} description={t("desc.domains")} />
      <ErrorBanner error={error} />
      {loading ? <Spinner /> : data && (
        <div className="space-y-6">
          {!data.provisioning_configured && (
            <Notice tone="warn" title="AWS domain provisioning is not configured" testId="provisioning-not-configured">
              AWS SES API credentials are not set on the server, so CARIO Mail cannot create SES identities or fetch DNS records. New domains stay pending.
              A platform administrator can confirm a domain that AWS already shows as verified (recorded in the audit log).
            </Notice>
          )}
          {can("admin") && (
            <form onSubmit={add} className="flex flex-col gap-2 sm:flex-row" data-testid="domain-add-form">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="mail.yourcompany.com" required aria-label="Domain name" className="font-mono" data-testid="domain-name-input" />
              <Button type="submit" disabled={busy} data-testid="domain-add-submit">Add domain</Button>
            </form>
          )}
          {data.domains.length === 0 ? <EmptyState icon={Globe2} title="No sending domains" body="Add a domain you own to start sending." testId="domains-empty" />
            : data.domains.map((d) => <DomainCard key={`${d.id}-${d.last_checked_at}`} d={d} canAdmin={can("admin")} provisioning={data.provisioning_configured} reload={reload} />)}
        </div>
      )}
    </div>
  );
}
